import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getErrorMessage,
    useMutationWithHandler,
} from '@/hooks/useMutationWithHandler'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ExternalLink, Loader2, RefreshCw, TriangleAlert } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog } from 'vnetwork-ui'
import {
    eInvoiceQueryKey,
    useEInvoiceErrorText,
} from '../../hooks/use-e-invoice'
import { previewEInvoice, publishEInvoice } from '../../services'
import { EInvoice, EInvoicePreview, EInvoicePublishPayload } from '../../types'
import {
    PDF_VIEW_PARAMS,
    formatEInvoiceAmount,
    formatEInvoiceDate,
    formatEInvoiceTime,
    formatExchangeRate,
} from '../../utils/e-invoice'
import EmailTagInput, { isValidEmail } from '../SignedDocument/EmailTagInput'

const EXCHANGE_RATE_REQUIRED = 'e-invoice-exchange-rate-required'

interface PublishEInvoiceDialogProps {
    open: boolean
    onClose: () => void
    invoiceID: number
}

const Field = ({
    label,
    className = '',
    children,
}: {
    label: string
    className?: string
    children: ReactNode
}) => (
    <div className={className}>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-gray-800">{children}</div>
    </div>
)

/** Tỷ giá nhập tay: cho số lẻ, dấu `,` hoặc `.`; trả undefined nếu không hợp lệ / ≤ 0 */
const parseExchangeRate = (value: string) => {
    const rate = Number(value.trim().replace(',', '.'))
    return value.trim() && rate > 0 ? rate : undefined
}

/**
 * Popup phát hành hoá đơn điện tử (MISA meInvoice): xem trước PDF do MISA render, xác nhận
 * người mua / tổng tiền, sửa email nhận, nhập tỷ giá (hoá đơn USD), rồi phát hành.
 * Nội dung dòng hàng sửa ở nút "Sửa nội dung" của khối "Hoá đơn điện tử" trước khi mở popup.
 * Mọi thay đổi email / tỷ giá phải "Cập nhật xem trước" trước khi phát hành, để thứ gửi đi
 * đúng thứ đang hiện.
 */
const PublishEInvoiceDialog = ({
    open,
    onClose,
    invoiceID,
}: PublishEInvoiceDialogProps) => {
    const { t } = useTranslation('invoice')
    const errorText = useEInvoiceErrorText()
    const queryClient = useQueryClient()

    const [preview, setPreview] = useState<EInvoicePreview>()
    const [previewError, setPreviewError] = useState('')
    const [emails, setEmails] = useState<string[]>([])
    // Đã nhận danh sách email mặc định từ backend chưa. Hoá đơn USD: lần xem trước đầu báo thiếu
    // tỷ giá nên chưa có → lần sau vẫn KHÔNG gửi receiver_emails (gửi [] = không gửi mail cho khách)
    const [emailsLoaded, setEmailsLoaded] = useState(false)
    // Hoá đơn USD: preview trả currency = USD + tỷ giá Vietcombank tự điền (sửa được). Nếu backend
    // không lấy được tỷ giá Vietcombank thì trả e-invoice-exchange-rate-required → ô tỷ giá trống
    const [needsRate, setNeedsRate] = useState(false)
    const [rateInput, setRateInput] = useState('')
    // Body của lần preview gần nhất → so với giá trị đang hiện để biết đã sửa chưa
    const [previewedBody, setPreviewedBody] = useState<EInvoicePublishPayload>()
    const [publishError, setPublishError] = useState('')
    const [now, setNow] = useState(Date.now())

    // Link xem trước hết hạn sau 5 phút: đếm giờ để hiện nút Tải lại xem trước
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 15_000)
        return () => window.clearInterval(timer)
    }, [])

    const isUSD = preview?.currency === 'USD'
    const showRate = needsRate || isUSD
    // Tỷ giá của bản xem trước đang hiện (Vietcombank hoặc nhập tay) — tỷ giá sẽ gửi khi phát hành
    const previewedRate = isUSD ? preview?.exchange_rate : undefined
    const exchangeRate = showRate ? parseExchangeRate(rateInput) : undefined
    const rateChanged = showRate && exchangeRate !== previewedRate
    const currentBody: EInvoicePublishPayload = {
        ...(emailsLoaded ? { receiver_emails: emails } : {}),
        // Chỉ gửi khi kế toán sửa / nhập tay; không đổi thì để backend lấy lại tỷ giá Vietcombank
        ...(rateChanged && exchangeRate ? { exchange_rate: exchangeRate } : {}),
    }

    const previewMutation = useMutationWithHandler(
        { mutationFn: previewEInvoice },
        {
            reactQueryKeys: QUERY_KEYS.INVOICE_E_INVOICE,
            showSuccessToast: false,
            showErrorToast: false,
            onSuccess: (response, variables) => {
                const result = response?.result
                if (!result) return
                setPreview(result)
                setPreviewError('')
                setPublishError('')
                // USD: ô tỷ giá luôn hiện đúng tỷ giá của bản xem trước (Vietcombank hoặc nhập tay)
                if (result.currency === 'USD' && result.exchange_rate)
                    setRateInput(String(result.exchange_rate))
                // Lần đầu (không gửi receiver_emails): prefill email mặc định của backend
                const emailsUsed =
                    variables.receiver_emails ?? result.receiver_emails ?? []
                if (!variables.receiver_emails) setEmails(emailsUsed)
                setEmailsLoaded(true)
                setPreviewedBody({ receiver_emails: emailsUsed })
            },
            onError: (error) => {
                if (getErrorMessage(error) === EXCHANGE_RATE_REQUIRED) {
                    // Không phải lỗi: hoá đơn USD, hiện ô nhập tỷ giá
                    setNeedsRate(true)
                    setPreview(undefined)
                    setPreviewError('')
                    return
                }
                setPreviewError(errorText(error))
            },
        }
    )

    // Mở popup: xem trước với email mặc định (không gửi receiver_emails)
    useEffect(() => {
        previewMutation.mutateAsync({ invoiceID })
    }, [invoiceID])

    const storeEInvoice = (eInvoice: EInvoice) =>
        queryClient.setQueryData(eInvoiceQueryKey(invoiceID), eInvoice)

    const refreshEInvoice = () =>
        queryClient.invalidateQueries({ queryKey: eInvoiceQueryKey(invoiceID) })

    const publishMutation = useMutationWithHandler(
        { mutationFn: publishEInvoice },
        {
            reactQueryKeys: QUERY_KEYS.INVOICE_E_INVOICE,
            showSuccessToast: false,
            showErrorToast: false,
            onSuccess: (response) => {
                const result = response?.result
                if (!result) return
                storeEInvoice(result)
                if (result.status === 'published') {
                    toast.success(
                        t('e-invoice-published-success', {
                            number: result.inv_no ?? '',
                        })
                    )
                    onClose()
                } else if (result.status === 'publishing') {
                    // Khối hoá đơn điện tử tự hỏi lại mỗi 10 giây
                    toast(t('e-invoice-publishing-hint'))
                    onClose()
                } else {
                    // failed: giữ popup, báo lỗi đỏ; sửa dữ liệu rồi phát hành lại
                    setPublishError(
                        t('e-invoice-publish-failed', {
                            message: result.error_message || '—',
                            code: result.error_code || '—',
                        })
                    )
                }
            },
            onError: (error) => {
                const axiosError = error as AxiosError
                if (!axiosError?.response) {
                    // Timeout / mất kết nối: có thể đã phát hành → KHÔNG gọi publish lại, đọc trạng thái
                    toast(t('e-invoice-publish-no-response'))
                    refreshEInvoice()
                    onClose()
                    return
                }
                // HTTP 400: chưa có gì được phát hành
                setPublishError(errorText(error))
                if (getErrorMessage(error) === 'e-invoice-already-issued')
                    refreshEInvoice()
            },
        }
    )

    const isPreviewing = previewMutation.isPending
    const isPublishing = publishMutation.isPending
    const hasInvalidEmail = emails.some((email) => !isValidEmail(email))
    const rateInvalid = showRate && !exchangeRate
    const isDirty =
        !previewedBody ||
        JSON.stringify(previewedBody.receiver_emails ?? []) !==
            JSON.stringify(emails) ||
        rateChanged
    const isExpired =
        !!preview?.preview_expires_at &&
        new Date(preview.preview_expires_at).getTime() <= now

    const canRefreshPreview =
        !isPreviewing && !isPublishing && !hasInvalidEmail && !rateInvalid
    const canPublish =
        !!preview &&
        !isDirty &&
        (!isUSD || !!previewedRate) &&
        !isPreviewing &&
        !isPublishing &&
        !hasInvalidEmail

    const refreshPreview = () =>
        previewMutation.mutateAsync({ invoiceID, ...currentBody })

    // Luôn gửi receiver_emails (kể cả []) để backend không tự lấy lại danh sách mặc định.
    // USD: BẮT BUỘC gửi tỷ giá đang hiện (tỷ giá Vietcombank có thể đã đổi từ lúc xem trước)
    const publish = () =>
        publishMutation.mutateAsync({
            invoiceID,
            receiver_emails: emails,
            ...(isUSD && previewedRate ? { exchange_rate: previewedRate } : {}),
        })

    const rateSourceNote = rateChanged
        ? t('e-invoice-exchange-rate-changed')
        : preview?.exchange_rate_source === 'vietcombank-transfer'
          ? t('e-invoice-exchange-rate-vietcombank', {
                time: formatEInvoiceTime(preview.exchange_rate_updated_at),
            })
          : preview?.exchange_rate_source === 'manual'
            ? t('e-invoice-exchange-rate-manual')
            : t('e-invoice-exchange-rate-enter')

    const renderPreview = () => {
        if (isPreviewing && !preview)
            return (
                <div className="flex h-[70vh] items-center justify-center text-gray-500">
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
            )
        if (previewError)
            return (
                <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {previewError}
                </div>
            )
        if (!preview)
            return (
                <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                    {t('e-invoice-preview-need-rate')}
                </div>
            )
        if (isExpired)
            return (
                <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-gray-300 p-6 text-sm text-gray-500">
                    {t('e-invoice-preview-expired')}
                    <Button
                        variant="outlined"
                        startIcon={<RefreshCw className="h-4 w-4" />}
                        onClick={refreshPreview}
                        disabled={!canRefreshPreview}
                        loading={isPreviewing}
                    >
                        {t('e-invoice-preview-reload')}
                    </Button>
                </div>
            )
        // Giống PDF chứng từ ký số: viewer của trình duyệt, rộng hết khung, ẩn toolbar + cột
        // thumbnail (URL fragment, không ảnh hưởng link MISA); mở tab mới có nút riêng ở footer
        return (
            <iframe
                title={t('e-invoice-preview')}
                src={`${preview.preview_url}${PDF_VIEW_PARAMS}`}
                className="h-[70vh] w-full border-0 bg-white"
            />
        )
    }

    return (
        <Dialog
            open={open}
            onClose={() => !isPublishing && onClose()}
            maxWidth="lg"
            title={t('e-invoice-publish-title')}
            actions={
                <div className="flex w-full flex-wrap items-center justify-end gap-2">
                    {isPublishing && (
                        <span className="mr-auto text-sm text-gray-500">
                            {t('e-invoice-publishing-wait')}
                        </span>
                    )}
                    {!isPublishing && isDirty && preview && (
                        <span className="mr-auto text-sm text-amber-700">
                            {t('e-invoice-preview-outdated')}
                        </span>
                    )}
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPublishing}
                    >
                        {t('cancel')}
                    </Button>
                    {preview && !isExpired && (
                        <Button
                            variant="outlined"
                            startIcon={<ExternalLink className="h-4 w-4" />}
                            onClick={() =>
                                window.open(
                                    preview.preview_url,
                                    '_blank',
                                    'noopener,noreferrer'
                                )
                            }
                        >
                            {t('e-invoice-open-new-tab')}
                        </Button>
                    )}
                    {(isDirty || !preview) && (
                        <Button
                            variant="outlined"
                            startIcon={<RefreshCw className="h-4 w-4" />}
                            onClick={refreshPreview}
                            disabled={!canRefreshPreview}
                            loading={isPreviewing}
                        >
                            {t('e-invoice-preview-update')}
                        </Button>
                    )}
                    <Button
                        onClick={publish}
                        disabled={!canPublish}
                        loading={isPublishing}
                    >
                        {t('e-invoice-publish-confirm')}
                    </Button>
                </div>
            }
        >
            <div className="space-y-3 text-sm">
                <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-amber-800">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    {t('e-invoice-publish-warning')}
                </div>

                {publishError && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-red-800">
                        {publishError}
                    </div>
                )}

                {preview && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border border-gray-200 px-4 py-3 md:grid-cols-4">
                        <Field label={t('e-invoice-series')}>
                            {preview.inv_series}
                        </Field>
                        <Field label={t('e-invoice-date')}>
                            {formatEInvoiceDate(preview.inv_date)}
                        </Field>
                        <Field
                            label={t('e-invoice-buyer')}
                            className="md:col-span-2"
                        >
                            {preview.buyer_name}
                            {preview.buyer_tax_code &&
                                ` · ${t('e-invoice-buyer-tax-code')}: ${preview.buyer_tax_code}`}
                            {preview.buyer_address && (
                                <div className="text-xs text-gray-500">
                                    {preview.buyer_address}
                                </div>
                            )}
                        </Field>
                        <Field label={t('e-invoice-amount-without-vat')}>
                            {formatEInvoiceAmount(
                                preview.total_amount_without_vat,
                                preview.currency
                            )}
                        </Field>
                        <Field label={t('e-invoice-vat-amount')}>
                            {formatEInvoiceAmount(
                                preview.total_vat_amount,
                                preview.currency
                            )}
                        </Field>
                        <Field label={t('e-invoice-total-amount')}>
                            <span className="font-semibold">
                                {formatEInvoiceAmount(
                                    preview.total_amount,
                                    preview.currency
                                )}
                            </span>
                        </Field>
                        <Field label={t('e-invoice-lines')}>
                            {preview.lines}
                        </Field>
                        <Field
                            label={t('e-invoice-amount-in-words')}
                            className="col-span-2 md:col-span-4"
                        >
                            <span className="italic">
                                {preview.total_amount_in_words}
                            </span>
                        </Field>
                        {isUSD && (
                            <Field
                                label={t('e-invoice-exchange-rate')}
                                className="col-span-2 md:col-span-4"
                            >
                                {t('e-invoice-exchange-summary', {
                                    rate: formatExchangeRate(
                                        preview.exchange_rate
                                    ),
                                    vnd: formatEInvoiceAmount(
                                        preview.total_amount_vnd,
                                        'VND'
                                    ),
                                })}
                            </Field>
                        )}
                    </div>
                )}

                <div
                    className={`grid gap-3 ${showRate ? 'md:grid-cols-[1fr_2fr]' : ''}`}
                >
                    {showRate && (
                        <label className="block">
                            <span className="mb-1 block font-medium text-gray-700">
                                {t('e-invoice-exchange-rate-input')}{' '}
                                <span className="text-red-500">*</span>
                            </span>
                            <input
                                value={rateInput}
                                onChange={(event) =>
                                    setRateInput(event.target.value)
                                }
                                inputMode="decimal"
                                placeholder="26130"
                                disabled={isPublishing}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none"
                            />
                            {rateInput.trim() && rateInvalid ? (
                                <span className="mt-1 block text-red-600">
                                    {t('e-invoice-exchange-rate-invalid')}
                                </span>
                            ) : (
                                // Nguồn tỷ giá của bản xem trước đang hiện
                                <span className="mt-1 block text-xs text-gray-500">
                                    {rateSourceNote}
                                </span>
                            )}
                        </label>
                    )}
                    <div>
                        <span className="mb-1 block font-medium text-gray-700">
                            {t('e-invoice-receiver-emails')}
                        </span>
                        <EmailTagInput
                            value={emails}
                            onChange={setEmails}
                            disabled={isPublishing}
                            placeholder={t(
                                'signed-document-mail-recipient-placeholder'
                            )}
                        />
                        <span className="mt-1 block text-xs text-gray-500">
                            {t('e-invoice-receiver-emails-hint')}
                        </span>
                        {hasInvalidEmail && (
                            <span className="mt-1 block text-red-600">
                                {t(
                                    'signed_document_errors.esign-mail-invalid-address'
                                )}
                            </span>
                        )}
                    </div>
                </div>

                {renderPreview()}
            </div>
        </Dialog>
    )
}

export default PublishEInvoiceDialog
